import { damagedMessageFrom } from '@papan/types'
import { partitionRefusalKey } from './protectedBranchFlow'
import { folderBeingDeletedText, reservedNameText } from './useDeleteRunner'

/**
 * What a failed move says — the ONE answer for every move surface (the drive's drop and paste
 * handlers; `moveItemsInto` behind the sidebar, cut-paste and the taskbar Drive icon). A known
 * refusal or a folder being deleted speaks the person's language; otherwise the server's own
 * reason (a name conflict, a folder into itself, …). Technical text a person can't act on — a
 * network failure (fetch's TypeError), an HTML error page parsed as JSON (SyntaxError, e.g. a
 * 502 mid-deploy), a timed-out request (AbortError), or no message at all — is "Move failed".
 */
/** #982: a cross-organisation move's refusals, by the code the server sends beside its English. */
const MOVE_REFUSAL_KEYS: Readonly<Record<string, string>> = {
  MOVE_SOURCE_NOT_REMOVABLE: 'driveView.toast.moveRefused.sourceReadOnly',
  CROSS_ORG_POINTER_MOVE: 'driveView.toast.moveRefused.pointerIntoShare',
  CROSS_ORG_WRITE_REFUSED: 'driveView.toast.moveRefused.accessChanged',
  MOVE_HALF_DONE: 'driveView.toast.moveRefused.halfDone',
  FOLDER_MOVE_NOTHING_REMOVABLE: 'driveView.toast.moveRefused.nothingRemovable',
  FOLDER_MOVE_PARTITION_CHANGED: 'driveView.toast.moveRefused.partitionChanged',
  FOLDER_MOVE_APP: 'driveView.toast.moveRefused.app',
  FOLDER_MOVE_CANNOT_CARRY: 'driveView.toast.moveRefused.cannotCarry',
  FOLDER_MOVE_CHANGED: 'driveView.toast.moveRefused.changed',
  FOLDER_MOVE_SOURCE_GONE: 'driveView.toast.moveRefused.sourceGone',
  FOLDER_MOVE_TAKEBACK_PENDING: 'driveView.toast.moveRefused.takeBackPending',
  FOLDER_MOVE_STATUS_UNKNOWN: 'driveView.toast.moveRefused.statusUnknown',
  // #1167 S4: a big move's job picked up too late to run — nothing moved; a job that failed outright.
  MOVE_JOB_EXPIRED: 'driveView.toast.moveRefused.jobExpired',
  // …and one whose outcome couldn't be learned (deadline passed / status gone): it may be queued, running, done or
  // never run — "couldn't confirm", never #982's "it finished" (`FOLDER_MOVE_STATUS_UNKNOWN`).
  MOVE_JOB_STATUS_UNKNOWN: 'driveView.toast.moveRefused.jobStatusUnknown',
  MOVE_JOB_FAILED: 'driveView.toast.moveFailed',
  // …and one whose folder was moved elsewhere between the 202 and the job's pickup — nothing moved.
  MOVE_SOURCE_MOVED: 'driveView.toast.moveRefused.sourceMoved',
  MOVE_SOURCE_CHANGED: 'driveView.toast.moveRefused.itemChanged',
  FOLDER_MOVE_TOO_LARGE: 'driveView.toast.moveRefused.tooLarge',
  FOLDER_MOVE_TOO_DEEP: 'driveView.toast.moveRefused.tooDeep',
  MOVE_COPY_KEPT: 'driveView.toast.moveRefused.itemCopyKept',
  MOVE_BOTH_REMAIN: 'driveView.toast.moveRefused.bothRemain',
  FOLDER_MOVE_STRUCTURAL: 'driveView.toast.moveRefused.structural',
  CROSS_ORG_POINTER_COPY: 'driveView.toast.moveRefused.pointerCopyIntoShare',
  FOLDER_MOVE_COPY_KEPT: 'driveView.toast.moveRefused.copyKept',
  FOLDER_MOVE_POINTER: 'driveView.toast.moveRefused.folderHoldsPointer',
  FOLDER_MOVE_DEST_GONE: 'driveView.toast.moveRefused.destGone',
}

export function moveErrorText(err: unknown, t: (key: string) => string, itemName?: string): string {
  // #1397 K1: a damaged stored copy — the server's own words (written for the person); in a loop of one toast per item,
  // the item is named when the server named no file
  const damaged = damagedMessageFrom(err)
  if (damaged) return !(err as { damagedName?: unknown }).damagedName && itemName ? `${itemName}: ${damaged}` : damaged
  const k = partitionRefusalKey(err, 'move')
  if (k) return t(k)
  const refusal = (err as { code?: unknown } | null)?.code
  if (typeof refusal === 'string' && MOVE_REFUSAL_KEYS[refusal]) return t(MOVE_REFUSAL_KEYS[refusal])
  const deleting = folderBeingDeletedText(err, t) ?? reservedNameText(err, t)
  if (deleting) return deleting
  const e = err as { name?: unknown; message?: unknown } | null
  const technical = err instanceof TypeError || err instanceof SyntaxError || e?.name === 'AbortError'
  const message = typeof e?.message === 'string' ? e.message : ''
  return technical || !message ? t('driveView.toast.moveFailed') : message
}
